import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { HOURS, toISODate, WEEKDAY_LABELS, MONTH_LABELS } from "../../utils/calendar";
import { useLanguage } from "../../i18n/LanguageContext";
import { COLORS } from "../../theme";

const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;

export default function CalendarWeekView({ days, bookings, isFullDay, onSelectSlot, onSelectBooking }) {
  const { lang } = useLanguage();

  const bookingFor = (day, hour) =>
    bookings.find((b) => b.date === toISODate(day) && (b.fullDay || (hour >= b.startHour && hour < b.endHour)));

  const dayLabel = (d) => {
    const wd = WEEKDAY_LABELS[lang][(d.getDay() + 6) % 7];
    return `${wd} ${d.getDate()} ${MONTH_LABELS[lang][d.getMonth()].slice(0, 3)}`;
  };

  if (isFullDay) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          {days.map((day) => {
            const booking = bookings.find((b) => b.date === toISODate(day));
            return (
              <Stack
                key={toISODate(day)}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => (booking ? onSelectBooking(booking) : onSelectSlot(day, null))}
                sx={{
                  p: 1.5, borderRadius: 1, border: `1px solid ${COLORS.line}`, cursor: "pointer",
                  bgcolor: booking ? COLORS.greenSoft : "background.paper",
                  "&:hover": { bgcolor: booking ? COLORS.greenSoft : COLORS.brassSoft, outline: booking ? `1px solid ${COLORS.green}` : "none" },
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {dayLabel(day)}
                </Typography>
                {booking ? (
                  <Chip size="small" label={booking.occupantId?.name} sx={{ bgcolor: COLORS.green, color: "#fff" }} />
                ) : (
                  <Chip size="small" variant="outlined" label="—" />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: `64px repeat(${days.length}, minmax(120px, 1fr))`, minWidth: days.length > 1 ? 700 : 300 }}>
        <Box sx={{ borderBottom: `1px solid ${COLORS.line}` }} />
        {days.map((d) => (
          <Box key={toISODate(d)} sx={{ p: 1, textAlign: "center", fontSize: 12, fontWeight: 600, borderBottom: `1px solid ${COLORS.line}`, borderLeft: `1px solid ${COLORS.line}` }}>
            {dayLabel(d)}
          </Box>
        ))}
        {HOURS.map((h) => (
          <Box key={h} sx={{ display: "contents" }}>
            <Box sx={{ p: 0.5, fontSize: 11, color: "text.secondary", textAlign: "right", pr: 1, borderBottom: `1px solid ${COLORS.line}` }}>
              {fmtHour(h)}
            </Box>
            {days.map((d) => {
              const b = bookingFor(d, h);
              const isStart = b && b.startHour === h;
              return (
                <Box
                  key={toISODate(d) + h}
                  onClick={() => (b ? onSelectBooking(b) : onSelectSlot(d, h))}
                  sx={{
                    minHeight: 34, borderBottom: `1px solid ${COLORS.line}`, borderLeft: `1px solid ${COLORS.line}`,
                    cursor: "pointer",
                    bgcolor: b ? COLORS.greenSoft : "transparent",
                    "&:hover": { bgcolor: b ? COLORS.greenSoft : COLORS.brassSoft, outline: b ? `1px solid ${COLORS.green}` : "none" },
                    display: "flex", alignItems: "center", px: 0.5,
                  }}
                >
                  {isStart && (
                    <Typography variant="caption" noWrap sx={{ color: COLORS.green, fontWeight: 600 }}>
                      {b.occupantId?.name}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
